import axios from 'axios'
import cloneDeep from 'lodash.clonedeep'
import { records, user } from '@/api'
import formatFilters from '@/utils/formatFilters'
import CUSTOM_FIELDS_TYPES from '@/constants/enums/CUSTOM_FIELDS_TYPES'
import userResponseMapper from '@/js/mappers/usersResponseMapper'
import { loadNotificationByRecords, markNotificationsRead } from "@/api/notifications"
import eventBus from "@/eventBus"
import { EVENT_BUS_EVENTS } from "@/constants/EVENT_BUS_EVENTS"
import { getRecordActivityStreamGroup, getRepliedByMessages } from '@/api/records'
import { buildCardsRequest } from '@/helpers/buildRecordListRequest'

export default {
  getRecords: function ({commit, dispatch, rootState}, filters) {
    commit('preloader', true)
    commit('errorUpdateRecord', {})
    const viewFiltersId = cloneDeep(filters.filterViewId)

    return records.getRecords({
      ...formatFilters.toObject(filters),
      persistFilters: filters.persistFilters,
    }, true)
      .then(({ data }) => {
        commit('setViewFiltersId', viewFiltersId)
        if (data.data.length && rootState?.user?.config?.notification_widget?.display) {
          loadNotificationByRecords(data.data.map((record) => record.id).join('|'))
            .then((notifications) => {
              commit('setRecords', data.data.map((record) => ({
                ...record,
                notificationsCount: notifications[record.id],
              })))
          }).catch(() => {
            commit('setRecords', data.data)
          }).finally(() => {
            dispatch('getRecordsUsers')
            commit('setViewFiltersId', data.meta.filterViewId || -1)
            commit('setPagination', data.meta)
            commit('preloader', false)
          })
        } else {
          commit('setRecords', data.data)
          dispatch('getRecordsUsers')
          commit('setViewFiltersId', data.meta.filterViewId || -1)
          commit('setPagination', data.meta)
          commit('preloader', false)
        }
      })
      .catch((e) => {
        if (!axios.isCancel(e)) {
          console.error(e)
        }
      })
  },

  getMoreRecords: function ({commit, state, dispatch, rootState}, filters) {
    commit('preloaderMore', true)
    const viewFiltersId = cloneDeep(filters.filterViewId)
    return records.getRecords({
      ...formatFilters.toObject(filters),
      persistFilters: filters.persistFilters,
    })
      .then(({ data }) => {
        commit('setViewFiltersId', viewFiltersId)
        if (data.data.length && rootState?.user?.config?.notification_widget?.display) {
          loadNotificationByRecords(data.data.map((record) => record.id).join('|')).then((notifications) => {
            commit('setRecords', [...state.records, ...data.data.map((record) => ({
              ...record,
              notificationsCount: notifications[record.id],
            }))])
          }).catch(() => {
            commit('setRecords', [...state.records, ...data.data])
          }).finally(() => {
            dispatch('getRecordsUsers')
            commit('setViewFiltersId', data.meta.filterViewId || -1)
            commit('preloaderMore', false)
          })
        } else {
          commit('setRecords', [...state.records, ...data.data])
          dispatch('getRecordsUsers')
          commit('setViewFiltersId', data.meta.filterViewId || -1)
          commit('preloaderMore', false)
        }
      })
      .catch((e) => {
        if (!axios.isCancel(e)) {
          console.error(e)
        }
      })
  },

  getCardRecords: function ({ commit, dispatch, rootState }, filters) {
    commit('preloader', true)
    commit('errorUpdateRecord', {})
    const viewFiltersId = cloneDeep(filters.filterViewId)
    const { payload, isEmptyResult } = buildCardsRequest(filters)

    // The status pills and an advanced status filter share one request key, so
    // a disjoint pair can only be answered with an empty list.
    if (isEmptyResult) {
      commit('setViewFiltersId', viewFiltersId)
      commit('setRecords', [])
      commit('setPagination', {
        total: 0,
        current_page: 1,
        last_page: 1,
        per_page: filters.per_page || 10,
      })
      commit('preloader', false)
      return Promise.resolve()
    }

    return records.getCardRecords(payload, true)
      .then(({ data }) => {
        commit('setViewFiltersId', viewFiltersId)
        if (data.data.length && rootState?.user?.config?.notification_widget?.display) {
          loadNotificationByRecords(data.data.map((record) => record.id).join('|'))
            .then((notifications) => {
              commit('setRecords', data.data.map((record) => ({
                ...record,
                notificationsCount: notifications[record.id],
              })))
            }).catch(() => {
              commit('setRecords', data.data)
            }).finally(() => {
              dispatch('getRecordsUsers')
              commit('setViewFiltersId', data.meta.filterViewId || -1)
              commit('setPagination', data.meta)
              commit('preloader', false)
            })
        } else {
          commit('setRecords', data.data)
          dispatch('getRecordsUsers')
          commit('setViewFiltersId', data.meta.filterViewId || -1)
          commit('setPagination', data.meta)
          commit('preloader', false)
        }
      })
      .catch((e) => {
        if (!axios.isCancel(e)) {
          console.error(e)
          commit('preloader', false)
        }
      })
  },

  getMoreCardRecords: function ({ commit, state, dispatch, rootState }, filters) {
    commit('preloaderMore', true)
    const viewFiltersId = cloneDeep(filters.filterViewId)
    const { payload, isEmptyResult } = buildCardsRequest(filters)

    if (isEmptyResult) {
      commit('preloaderMore', false)
      return Promise.resolve()
    }

    return records.getCardRecords(payload)
      .then(({ data }) => {
        commit('setViewFiltersId', viewFiltersId)
        if (data.data.length && rootState?.user?.config?.notification_widget?.display) {
          loadNotificationByRecords(data.data.map((record) => record.id).join('|')).then((notifications) => {
            commit('setRecords', [...state.records, ...data.data.map((record) => ({
              ...record,
              notificationsCount: notifications[record.id],
            }))])
          }).catch(() => {
            commit('setRecords', [...state.records, ...data.data])
          }).finally(() => {
            dispatch('getRecordsUsers')
            commit('setViewFiltersId', data.meta.filterViewId || -1)
            commit('preloaderMore', false)
          })
        } else {
          commit('setRecords', [...state.records, ...data.data])
          dispatch('getRecordsUsers')
          commit('setViewFiltersId', data.meta.filterViewId || -1)
          commit('preloaderMore', false)
        }
      })
      .catch((e) => {
        if (!axios.isCancel(e)) {
          console.error(e)
          commit('preloaderMore', false)
        }
      })
  },

  getRecordsUsers({ getters, commit }) {
    const userIds = getters.getFormattedRecords
      .reduce((prev, current) => ([
        ...current.customFields,
        ...prev,
      ]), [])
      .filter((field) => field.type === CUSTOM_FIELDS_TYPES.user && field.response)
      .map((field) => field.response)
    const lastModifiedIds = getters.getFormattedRecords
      .filter((record) => record.lastModifiedBy)
      .map((record) => String(record.lastModifiedBy))
    const uniqueUserIds = [...new Set([...userIds, ...lastModifiedIds])]
    if (uniqueUserIds.length) {
      user.getUsers({ company: sessionStorage.getItem('currentCompanySlug'), params: {
        userIds: uniqueUserIds.join('|'),
        per_page: uniqueUserIds.length <= 10
          ? 10
          : uniqueUserIds.length,
        fields: 'user[id,firstName,lastName]',
        with_hidden: true,
      }}).then(({ data: { data: usersResponse } }) => {
        commit('setRecordsUsers', userResponseMapper(usersResponse))
      })
    }
  },
  getRecordUsers({ getters, commit }) {
    const userIds = getters.getFormattedRecord.customFields
      .filter((field) => field.type === CUSTOM_FIELDS_TYPES.user && field.response)
      .map((field) => field.response)
    const lastModifiedIds = String(getters.getFormattedRecord?.lastModifiedBy)
    const uniqueUserIds = [...new Set([...userIds, lastModifiedIds])]
    if (uniqueUserIds.length) {
      user.getUsers({ company: sessionStorage.getItem('currentCompanySlug'), params: {
        userIds: [...uniqueUserIds].join('|'),
        per_page: uniqueUserIds.length <= 10
          ? 10
          : uniqueUserIds.length,
        fields: 'user[id,firstName,lastName]',
        with_hidden: true,
      }}).then(({ data: { data: usersResponse } }) => {
        commit('setRecordUsers', userResponseMapper(usersResponse))
      })
    }
  },
  updateRecordsRedDots({ state, commit }) {
    const ids = state.records.map((record) => record.id)
    if (ids.length) {
      commit('setRecords', state.records)
      return loadNotificationByRecords(ids.join('|')).then((notifications) => {
        commit('setRecords', state.records.map((record) => ({
          ...record,
          notificationsCount: notifications[record.id],
        })))
      })
    }
    return null
  },
  exportRecords({ commit }, payload) {
    commit('preloaderExport', true)
    return records.exportRecords(payload)
      .finally((res) => {
        commit('preloaderExport', false)
        return res
      })
  },
  editRecord({ commit, dispatch }, payload) {
    commit('preloaderSaving', true)
    return records.editRecord({ id: payload.id, payload: { ...payload.payload, fields: 'quote[id,author[avatar,id,currentSupplier,firstName,lastName],collaborators[firstName,lastName,email,id,avatar],createdDate,description,dueDate,name,status,statusLastChangedAt,updatedDate,recordTypeId,customFields[hasHistory,slug,id,quoteId,response],lastModifiedBy,meta]' } }, undefined, payload.ignoreEventBus)
      .then((data) => {
        commit('updateRecordItem', { id: payload.id, isSingle: payload.isSingle, data: data.data })
        commit('clearUpdateRecordErrorById', payload.id)
        dispatch('getRecordUsers')
        return data
      }).catch((error) => {
        commit('errorUpdateRecord', { id: payload.id, error })
        commit('preloader', false)
        throw error
      })
      .finally(() => {
        commit('preloaderSaving', false)
        commit('preloader', false)
      })
  },
  editRecords({ commit, dispatch }, {
    id, payload, ignoreEventBus = false,
  }) {
    commit('preloaderSaving', true)
    return records.editRecord({ id, payload }, undefined, ignoreEventBus)
      .then(({ data }) => {
        commit('updateRecordItem', { id, data: data.data, isSingle: false })
        if ('isLast' in payload && !payload.isLast) {
          return data
        }

        dispatch('getRecordsUsers')
        return data
      }).catch((error) => {
        commit('errorUpdateRecord', { id, error })
        commit('preloader', false)
        commit('preloaderSaving', false)
        throw error
      }).finally(() => {
        commit('preloader', false)
        commit('preloaderSaving', false)
      })
  },
  createRecord({ commit }, {
    company,
    payload,
  }) {
    commit('preloader', true)
    return records.createRecord(company, payload)
      .then((data) => data)
      .catch((err) => {
        commit('preloader', false)
        throw err
      }).finally(() => {
        commit('preloader', false)
      })
  },
  getRecord({ commit, dispatch }, id) {
    commit('preloader', true)
    commit('errorUpdateRecord', {})
    return records.getRecord(id, { fields: 'quote[id,author[avatar,id,currentSupplier,firstName,lastName],createdDate,description,dueDate,name,status,statusLastChangedAt,updatedDate,recordTypeId,customFields[slug,id,response,hasHistory],lastModifiedBy,meta]' })
      .then(({ data }) => {
        commit('setRecordMeta', data.meta)
        commit('setRecord', data.data)
        dispatch('getRecordUsers')
      })
      .finally(() => {
        markNotificationsRead(id, [2]).then(() => {
          eventBus.$emit(EVENT_BUS_EVENTS.ACTIVITY_STREAM_LOAD)
        })
        commit('preloader', false)
      })
  },
  getRecordSilent({ commit, dispatch }, id) {
    commit('errorUpdateRecord', {})
    return records.getRecord(id, { fields: 'quote[id,author[avatar,id,currentSupplier,firstName,lastName],createdDate,description,dueDate,name,status,statusLastChangedAt,updatedDate,recordTypeId,customFields[slug,id,response,hasHistory],lastModifiedBy,meta]' })
      .then(({ data }) => {
        commit('setRecord', data.data)
        commit('setRecordMeta', data.meta)
        dispatch('getRecordUsers')
      })
  },
  readRecordStatuses: ({ commit, state }, company) => {
    commit('setPendingStatusesCompany', company)

    return records.readRecordStatuses(company)
      .then(({ data }) => {
        // A company switch resets the store but cannot cancel this request, so a
        // late response would cache the previous company's statuses under the new
        // one. Commit only while this is still the read the store is waiting for:
        // a newer read - or the reset itself - supersedes it.
        if (state.pendingStatusesCompany !== company) {
          return
        }

        commit('setStatuses', { company, statuses: data.data })
      })
  },
  updateRecordStatus({ commit, dispatch }, { id, status, isLast = true, isSingle, ignoreEventBus = false }) {
    commit('preloaderSaving', true)
    commit('activityStreamLoader', true)
    return records.updateRecordStatus({ id, status, fields: 'quote[id,author[avatar,id,currentSupplier,firstName,lastName],collaborators[firstName,lastName,email,id,avatar],createdDate,description,dueDate,name,status,statusLastChangedAt,updatedDate,recordTypeId,customFields[slug,id,response,hasHistory],lastModifiedBy]' }, ignoreEventBus)
      .then(({ data }) => {
        commit('updateRecordItem', { id, data: data.data, isSingle })
        if (!isLast || !isSingle) {
          return
        }
        dispatch('getRecordActivityStream', { id, showLoader: false, clearPrevious: false })
      })
      .finally(() => {
        commit('preloaderSaving', false)
        commit('activityStreamLoader', false)
      })
  },
  updateRecordType({ commit, dispatch }, { id, recordType, isLast = true, isSingle, ignoreEventBus = false }) {
    commit('preloaderSaving', true)
    commit('activityStreamLoader', true)
    return records.updateRecordType({ id, recordType, fields: 'quote[id,author[avatar,id,currentSupplier,firstName,lastName],collaborators[firstName,lastName,email,id,avatar],createdDate,description,dueDate,name,status,statusLastChangedAt,updatedDate,recordTypeId,customFields[slug,id,response,hasHistory],lastModifiedBy]' }, ignoreEventBus)
      .then(({ data }) => {
        commit('updateRecordItem', { id, data: data.data, isSingle })
        if (!isLast || !isSingle) {
          return
        }
        dispatch('getRecordActivityStream', { id, showLoader: false, clearPrevious: false })
      })
      .finally(() => {
        commit('preloaderSaving', false)
        commit('activityStreamLoader', false)
      })
  },
  getRecordActivityStream({ commit, state }, payload) {
    const {
      id,
      showLoader = true,
      clearPrevious = true,
    } = payload

    if (showLoader) {
      commit('activityStreamLoader', true)
    }
    if (clearPrevious) {
      commit('setActivityStream', [])
    }
    commit('setActivityFirstPage')
    return records.getRecordActivityStream(id, { page: state.activityStreamPage.currentPage })
      .then(({ data }) => {
        const isNeedPostGlobalEvent = data.data.flatMap(({ posts }) => posts).some((post) => {
          return !post.meta.seen
        })
        if (isNeedPostGlobalEvent) {
          eventBus.$emit(EVENT_BUS_EVENTS.ACTIVITY_STREAM_LOAD)
        }
        commit('setActivityStreamPage', data.meta)
        commit('setActivityStream', data.data)
      }).finally(() => {
        commit('activityStreamLoader', false)
      })
  },

  handleActivityStreamSocketEvent({ state, commit }, payload) {
    const index = state.activityStream
      .findIndex(({ uuid }) => uuid === payload.messageGroupUuid)

    const isGroupExist = index !== -1
    if (payload.type === 'created' && isGroupExist) {
      return Promise.resolve({
        refresh: false,
        payload: null,
      })
    }

    if (payload.type === 'created') {
      return getRecordActivityStreamGroup(payload.recordId, payload.messageGroupUuid).then(({ data: { data } }) => {
        commit('addActivityStreamPost', data)

        return {
          refresh: true,
          payload: data,
        }
      })
    }

    return getRecordActivityStreamGroup(payload.recordId, payload.messageGroupUuid).then(({ data: { data } }) => {
      commit('updateActivityStreamPost', data)

      return {
        refresh: true,
        payload: data,
      }
    })
  },

  handleActivityStreamReactionSocketEvent({ commit }, payload) {
    return getRecordActivityStreamGroup(payload.recordId, payload.messageGroupId).then(({ data: { data } }) => {
      commit('updateActivityStreamPost', data)

      return {
        refresh: true,
        payload: data,
      }
    })
  },

  handleActivityStreamFileSocketEvent({ commit }, payload) {
    commit('updateCanSummary', payload)
  },

  refreshStatus({ commit }, id) {
    commit('errorUpdateRecord', {})
    records.getRecord(id, { fields: 'quote[status,statusLastChangedAt,meta]' })
      .then(({ data }) => {
        commit('setRecordStatus', data.data)
      })
  },

  createMessage({ commit, dispatch, rootState }, payload) {
    commit('preloaderSaving', true)
    commit('messageSending', true)
    return records.createMessage(payload, rootState.user.currentUser.email)
      .then(() => {
        dispatch('getRecordActivityStream', { id: payload.id, showLoader: false })
        dispatch('getRecordFiles', { id: payload.id })
        dispatch('getRecordFileGroups', payload.id)
      }).finally(() => {
        commit('preloaderSaving', false)
        commit('messageSending', false)
      })
  },
  updateMessage({ commit, dispatch }, payload) {
    commit('preloaderSaving', true)
    commit('messageUpdating', true)
    return records.updateMessage(payload)
      .then(() => {
        dispatch('getRecordActivityStream', { id: payload.id, showLoader: false })
        dispatch('getRecordFiles', { id: payload.id })
        dispatch('getRecordFileGroups', payload.id)
      }).finally(() => {
        commit('preloaderSaving', false)
        commit('messageUpdating', false)
      })
  },
  createIssue({ commit, dispatch }, payload) {
    commit('issueCreating', true)
    return records.createIssue(payload)
      .then(() => {
        dispatch('getRecordActivityStream', { id: payload.id })
      }).finally(() => {
        commit('issueCreating', false)
      })
  },
  confirmRecordFulfillment({ dispatch }, payload) {
    return records.confirmRecordFulfillment(payload)
      .then(() => {
        dispatch('getRecord', payload.orderId)
        dispatch('getRecordActivityStream', { id: payload.orderId })
      })
  },
  getRecordFiles({ state, commit }, payload) {
    return records.getRecordFiles(payload)
      .then(({ data }) => {
        const clonedRecordFiles = state.recordFiles

        commit('setRecordFiles', data.data)
        commit('setFilesRelatedUsers', data.meta)

        if (!clonedRecordFiles.length) {
          return
        }

        const existingIds = clonedRecordFiles.map((file) => file.id)
        const newFiles = data.data.filter((file) => !existingIds.includes(file.id)).map((file) => {
          return {
            file_id: file.id,
            status: 'processing',
          }
        })

        newFiles.forEach((file) => {
          commit('updateCanSummary', file)
        })
      })
  },
  getRecordFileGroups({ commit }, id) {
    return records.getRecordFileGroups(id)
      .then(({ data }) => {
        commit('setRecordFileGroups', data.data)
      })
  },
  addCollaborators({ commit }, data) {
    commit('preloaderCollaborators', true)
    return records.addCollaborators(data).then(({data}) => {
      commit('setCollaborators', data.data)
      return data.data
    }).catch((err) => {
      commit('preloaderCollaborators', false)
      throw err
    }).finally(() => {
      commit('preloaderCollaborators', false)
    })
  },
  removeCollaborators({ commit }, data) {
    return records.removeCollaborators(data).then(({data}) => {
      commit('setCollaborators', data.data)
      return data.data
    })
  },
  getFiltersStructure({ commit, state }, isPersist = true) {
    if (Object.keys(state.filtersSettings).length && !isPersist) {
      const clonedData = cloneDeep(state.filtersSettings)
      if (clonedData?.data?.lastRequestQueryLog?.data) {
        clonedData.data.lastRequestQueryLog.data = state.viewFilters
      }
      commit('addUserSettingFilters', clonedData)
      return null
    }
    return records.getFiltersStructure()
      .then(({ data }) => {
        commit('addFilters', data.data.options)
        commit('addAdvancedOptions', data.data.filteringOptions)
        const clonedData = cloneDeep(data)
        if (!isPersist && clonedData?.data?.lastRequestQueryLog?.data) {
          clonedData.data.lastRequestQueryLog.data = state.viewFilters
        }
        commit('addUserSettingFilters', clonedData)
      })
  },
  updateRecordFilter({ commit }, dataFilter) {
    const data = Object.fromEntries(Object.entries(dataFilter).filter(([, field]) => field !== null))
    commit('setFilter', data)
  },
  editObjectChild({ dispatch }, payload) {
    return records.editObjectChild(payload).then(() => {
      dispatch('getRecord', payload.id)
    })
  },
  createObjectChilds({ commit }, payload) {
    commit('preloaderSaving', true)
    return records.createObjectChilds(payload).catch((err) => {
      commit('preloaderSaving', false)
      throw err
    })
      .finally(() => {
        commit('preloaderSaving', false)
      })
  },
  deleteObjectChild({ commit, dispatch, getters }, payload) {
    commit('preloaderSaving', true)
    return records.deleteObjectChild(payload).then((data) => {
      dispatch('getRecordSilent', getters.getFormattedRecord.id)
      return data
    }).catch((err) => {
      commit('preloaderSaving', false)
      throw err
    })
      .finally(() => {
        commit('preloaderSaving', false)
      })
  },
  getObjectChildrenFields(_, payload) {
    return records.getObjectChildren(payload).catch((err) => {
      throw err
    })
  },
  getObjectChildrenMeta(_, payload) {
    return records.getObjectChildrenMeta(payload)
  },
  patchObjectField({ commit }, payload) {
    commit('preloaderSaving', true)
    return records.patchObjectField(payload)
      .catch((err) => {
        commit('preloaderSaving', false)
        throw err
      })
      .finally(() => {
        commit('preloaderSaving', false)
      })
  },
  deleteRecord({ commit }, payload) {
    commit('preloaderSaving', true)
    return records.deleteRecord(payload)
      .catch((err) => {
        commit('preloaderSaving', false)
        throw err
      })
      .finally(() => {
        commit('preloaderSaving', false)
      })
  },
  loadPersistentColumns({ commit }) {
    return records.getPersistentColumns().then(({ data }) => {
      commit('setPersistentColumns', data)
    })
  },
  savePersistentColumns(ctx, payload) {
    return records.savePersistentColumns(payload)
  },
  setTemplateDocumentsList({ commit }, payload) {
    return records.templateDocumentsList(payload).then((res) => {
      commit('setTemplateDocumentsList', res.data.data)
    }).catch((err) => {
      throw err
    })
  },
  deleteFileFromRecord(context, payload) {
    return records.deleteFileFromRecord(payload).then(() => {
      context.commit('deleteRecordFile', payload.fileId)
    })
  },
  restoreRecordFile({ commit }, payload) {
    return records.restoreRecordFile(payload)
  },

  getRecordCustomFieldsConfiguration: ({ commit, rootGetters }) => {
    const promises = [
      records.loadRecordCustomFieldsConfiguration(),
      records.loadRecordSections(),
    ]

    if (rootGetters['user/isAiSummaryEnabled']) {
      promises.push(records.getVAiFieldConfiguration())
    }

    return Promise.all(promises).then(([
      customFieldsConfiguration,
      sections,
      aiConfig,
    ]) => {
      const vaiFieldsConfig = aiConfig?.data || []
      if (vaiFieldsConfig) {
        const vaiFieldsConfigRecord = vaiFieldsConfig.reduce((acc, current) => {
          const splitSlug = current.slug.split(':')
          const slug = splitSlug.slice(1, splitSlug.length).join(':')

          acc[slug] = current

          return acc
        }, {})

        commit('setCustomFieldsConfiguration', customFieldsConfiguration.data.data.map((field) => {
          const aiConfiguration = vaiFieldsConfigRecord[field.slug] ? {
            aiHelpText: vaiFieldsConfigRecord[field.slug].prompt,
            isIgnoredWhenAIParsing: vaiFieldsConfigRecord[field.slug].ignoreWhenParsing,
          } : {
            aiHelpText: '',
            isIgnoredWhenAIParsing: false,
          }

          return {
            ...field,
            ...aiConfiguration,
            objectConfiguration: field.objectConfiguration?.length ? field.objectConfiguration.map((objectField) => {
              const objectSlug = [field.slug, objectField.name].join(':')
              const objectAiConfiguration = vaiFieldsConfigRecord[objectSlug] ? {
                aiHelpText: vaiFieldsConfigRecord[objectSlug].prompt,
                isIgnoredWhenAIParsing: vaiFieldsConfigRecord[objectSlug].ignoreWhenParsing,
              } : {
                aiHelpText: '',
                isIgnoredWhenAIParsing: false,
              }

              return {
                ...objectField,
                ...objectAiConfiguration,
              }
            }) : field.objectConfiguration,
          }
        }))
        commit('setSections', sections.data.data)
        return
      }
      commit('setCustomFieldsConfiguration', customFieldsConfiguration.data.data)
      commit('setSections', sections.data.data)
    })
  },
  loadFilterViews: ({ commit }) => {
    commit('setFilterViewsLoaded', false)
    return records.loadFiltersViews().then(({ data: { data } }) => {
      commit('setFilterViews', data.map((view) => {
        return {
          ...view,
          count: null,
          isCountLoading: false,
        }
      }))
      commit('setFilterViewsLoaded', true)
    })
  },
  async fetchActivityStream({ commit, state }, id, showLoader = true) {
    if (state.activityStream.length >= state.activityStreamPage.total || state.isActivityStreamLoading) {
      return null
    }
    if (showLoader) {
      commit('activityStreamLoader', true)
    }
    return records.getRecordActivityStream(id, {
      page: state.activityStreamPage.currentPage + 1,
    })
      .then(({ data }) => {
        const isNeedPostGlobalEvent = data.data.flatMap(({ posts }) => posts).some((post) => {
          return !post.meta.seen
        })
        if (isNeedPostGlobalEvent) {
          eventBus.$emit(EVENT_BUS_EVENTS.ACTIVITY_STREAM_LOAD)
        }
        commit('setActivityStream', [...state.activityStream, ...data.data])
        commit('setActivityStreamPage', data.meta)
        return data
      }).finally(() => {
        commit('activityStreamLoader', false)
      })
  },
  getCollaborators({commit}, payload) {
    return records.getRecordCollaborators(payload)
      .then(({ data }) => {
        commit('setCollaborators', data.data)
      }).catch((err) => {
        throw err
      })

  },
  requestTemplateSignature({ commit }, payload) {
    commit('preloader', true)
    return records.signTemplate(payload)
      .catch((err) => {
        commit('preloader', false)
        throw err
      }).finally(() => {
        commit('preloader', false)
      })
  },
  createEmbedLinkToSignTemplate({ commit }, payload) {
    commit('preloader', true)
    return records.createEmbedLinkToSignTemplate(payload)
      .catch((err) => {
        commit('preloader', false)
        throw err
      }).finally(() => {
        commit('preloader', false)
      })
  },
  calculateFields({ commit }, payload) {
    return records.calculateFields(payload)
      .then(({ data }) => {
        commit('setCalculations', data)
      }).catch((err) => {
        throw err
      })
  },
  getFilesHasSummary({ commit }, payload) {
    return records.getFilesHasSummary(payload)
      .then(({ data }) => {
        commit('setFilesHasSummary', data)
      }).catch((err) => {
        throw err
      })
  },
  getFilesSummaryStatus({ commit }, payload) {
    return records.getFilesSummaryStatus(payload)
      .then(({ data }) => {
        commit('setFilesSummaryStatus', data)
      }).catch((err) => {
        throw err
      })
  },

  getRepliedByMessages(ctx, { recordId, messageGroupIds }) {
    return getRepliedByMessages(recordId, {
      messageGroupIds,
    }).then(({ data: { data } }) => {
      return (data || []).map(({ uuid, posts, replyTo, repliedBy }) => ({
        uuid,
        replyTo: {
          uuid: replyTo?.uuid,
          streams: replyTo?.posts,
        },
        repliedBy: repliedBy,
        streams: posts,
        created: posts[0]?.created,
        type: posts[0]?.type,
      }))
    })
  },
}
